/**
 * Strip flat empty-pad colors from TiTiler index previews (yellow/orange rectangle)
 * so the report shows crop colors on a black background.
 */

const ZERO_PAD_COLORS: Array<[number, number, number]> = [
  [253, 216, 132], // ExG
  [244, 109, 67], // NDVI
  [248, 139, 81], // SAVI
  [253, 174, 97],
];
const PAD_DIST2 = 22 * 22;

function isPadPixel(r: number, g: number, b: number): boolean {
  if (r > 248 && g > 248 && b > 248) return true;
  for (const [pr, pg, pb] of ZERO_PAD_COLORS) {
    const dr = r - pr;
    const dg = g - pg;
    const db = b - pb;
    if (dr * dr + dg * dg + db * db <= PAD_DIST2) return true;
  }
  // Cream-yellow empty pad (ExG family)
  if (r >= 245 && g >= 200 && b >= 110 && b <= 210 && r - b >= 30 && g - b >= 20) {
    return true;
  }
  return false;
}

/** Load a preview URL and return a PNG data URL with pad pixels made transparent. */
export async function cleanDroneIndexPreviewToDataUrl(
  previewUrl: string,
  signal?: AbortSignal
): Promise<string | null> {
  try {
    const res = await fetch(previewUrl, { credentials: 'include', signal });
    if (!res.ok) return null;
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);

    const dataUrl = await new Promise<string | null>((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(null);
            return;
          }
          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const d = imageData.data;
          for (let i = 0; i < d.length; i += 4) {
            if (isPadPixel(d[i], d[i + 1], d[i + 2])) {
              d[i + 3] = 0;
            }
          }
          ctx.putImageData(imageData, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch {
          resolve(null);
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      };
      img.src = objectUrl;
    });

    return dataUrl;
  } catch {
    return null;
  }
}
